import {
  collection,
  getDocs,
  getFirestore,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";

import { DashboardContext } from "context/Context";

const COST_KEYS = ["magnaCost", "premiumCost", "dieselCost"];

export default function CloneHistoricalCostsModal({
  show,
  onHide,
  businessLocations,
  defaultSourceLocId,
  onDone,
}) {
  const { user } = useContext(DashboardContext);
  const db = getFirestore();

  const [sourceLocId, setSourceLocId] = useState(defaultSourceLocId || "");
  const [targetLocIds, setTargetLocIds] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (show) {
      setSourceLocId(defaultSourceLocId || "");
      setTargetLocIds([]);
      setFromDate("");
      setToDate("");
      setOverwrite(false);
      setPreview(null);
    }
  }, [show, defaultSourceLocId]);

  const sourceOptions = useMemo(
    () =>
      (businessLocations || [])
        .filter((l) => l && l.locId)
        .map((l) => ({ value: l.locId, label: l.name })),
    [businessLocations]
  );

  const targetLocations = useMemo(
    () =>
      (businessLocations || []).filter(
        (l) => l && l.locId && l.locId !== sourceLocId
      ),
    [businessLocations, sourceLocId]
  );

  const toggleTarget = (locId) => {
    setTargetLocIds((prev) =>
      prev.includes(locId) ? prev.filter((id) => id !== locId) : [...prev, locId]
    );
    setPreview(null);
  };

  const buildQuery = () => {
    const ref = collection(db, "companies", user.companyId, "dayInformation");
    const constraints = [];
    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      constraints.push(where("date", ">=", start));
    }
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      constraints.push(where("date", "<=", end));
    }
    return constraints.length > 0 ? query(ref, ...constraints) : ref;
  };

  const computePlan = (docs) => {
    let totalDaysWithSource = 0;
    let totalWrites = 0;
    const perTarget = {};
    targetLocIds.forEach((id) => {
      perTarget[id] = 0;
    });

    docs.forEach((d) => {
      const data = d.data();
      const sourceData = data[sourceLocId];
      if (!sourceData) return;

      const hasAnyCost = COST_KEYS.some(
        (k) => sourceData[k] !== undefined && sourceData[k] !== null
      );
      if (!hasAnyCost) return;
      totalDaysWithSource++;

      targetLocIds.forEach((targetId) => {
        const existing = data[targetId] || {};
        const willWrite = COST_KEYS.some((k) => {
          const srcVal = sourceData[k];
          if (srcVal === undefined || srcVal === null) return false;
          if (overwrite) return true;
          return existing[k] === undefined || existing[k] === null;
        });
        if (willWrite) {
          perTarget[targetId] += 1;
          totalWrites += 1;
        }
      });
    });

    return { totalDaysWithSource, totalWrites, perTarget };
  };

  const handlePreview = async () => {
    if (!sourceLocId || targetLocIds.length === 0) {
      toast.error("Selecciona origen y al menos una estación destino");
      return;
    }
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toast.error("La fecha 'Desde' no puede ser mayor que 'Hasta'");
      return;
    }
    setPreviewLoading(true);
    try {
      const snap = await getDocs(buildQuery());
      setPreview(computePlan(snap.docs));
    } catch (e) {
      toast.error(e.message);
    }
    setPreviewLoading(false);
  };

  const handleClone = async () => {
    if (!sourceLocId || targetLocIds.length === 0) {
      toast.error("Selecciona origen y al menos una estación destino");
      return;
    }
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toast.error("La fecha 'Desde' no puede ser mayor que 'Hasta'");
      return;
    }

    setLoading(true);
    try {
      const snap = await getDocs(buildQuery());
      let daysWritten = 0;

      for (const d of snap.docs) {
        const data = d.data();
        const sourceData = data[sourceLocId];
        if (!sourceData) continue;

        const hasAnyCost = COST_KEYS.some(
          (k) => sourceData[k] !== undefined && sourceData[k] !== null
        );
        if (!hasAnyCost) continue;

        const updatePayload = {};
        let willWrite = false;

        targetLocIds.forEach((targetId) => {
          const existing = data[targetId] || {};
          const payload = {};
          COST_KEYS.forEach((k) => {
            const srcVal = sourceData[k];
            if (srcVal === undefined || srcVal === null) return;
            if (overwrite || existing[k] === undefined || existing[k] === null) {
              payload[k] = srcVal;
            }
          });
          if (Object.keys(payload).length > 0) {
            updatePayload[targetId] = payload;
            willWrite = true;
          }
        });

        if (willWrite) {
          await setDoc(d.ref, updatePayload, { merge: true });
          daysWritten++;
        }
      }

      toast.success(
        daysWritten === 0
          ? "No hubo costos para clonar con los filtros seleccionados"
          : `Costos clonados en ${daysWritten} día${daysWritten === 1 ? "" : "s"}`
      );
      if (onDone) onDone();
      onHide();
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const locName = (id) => {
    const l = (businessLocations || []).find((x) => x.locId === id);
    return l ? l.name : id;
  };

  return (
    <Modal centered show={show} onHide={onHide} size="lg">
      <Modal.Header className="fs-5 fw-semi-bold">
        Clonar histórico de costos entre estaciones
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label fw-bold">Estación origen</label>
          <Select
            classNamePrefix="react-select"
            isClearable={false}
            placeholder="Selecciona la estación origen"
            value={
              sourceOptions.find((o) => o.value === sourceLocId) || null
            }
            options={sourceOptions}
            onChange={(opt) => {
              setSourceLocId(opt?.value || "");
              setTargetLocIds((prev) => prev.filter((id) => id !== opt?.value));
              setPreview(null);
            }}
          />
          <small className="text-muted">
            Se leerán los costos (Regular, Premium y Diésel) ya guardados de esta
            estación.
          </small>
        </div>

        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="form-label fw-bold mb-0">Estaciones destino</label>
            {targetLocations.length > 0 && (
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 me-3"
                  onClick={() => {
                    setTargetLocIds(targetLocations.map((l) => l.locId));
                    setPreview(null);
                  }}
                >
                  Seleccionar todas
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => {
                    setTargetLocIds([]);
                    setPreview(null);
                  }}
                >
                  Limpiar
                </button>
              </div>
            )}
          </div>
          {!sourceLocId ? (
            <small className="text-muted">
              Selecciona primero la estación origen.
            </small>
          ) : targetLocations.length === 0 ? (
            <small className="text-muted">
              No hay otras estaciones disponibles.
            </small>
          ) : (
            <div className="d-flex flex-wrap">
              {targetLocations.map((loc) => (
                <div key={loc.locId} className="form-check me-3 mb-1">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`hist-clone-${loc.locId}`}
                    checked={targetLocIds.includes(loc.locId)}
                    onChange={() => toggleTarget(loc.locId)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`hist-clone-${loc.locId}`}
                  >
                    {loc.name}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Rango de fechas (opcional)</label>
          <div className="d-flex flex-row align-items-end">
            <div className="me-3">
              <label className="form-label mb-1">Desde</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPreview(null);
                }}
              />
            </div>
            <div>
              <label className="form-label mb-1">Hasta</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPreview(null);
                }}
              />
            </div>
          </div>
          <small className="text-muted">
            Si dejas ambas vacías se clona todo el historial disponible.
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">
            Si la estación destino ya tiene costos en una fecha
          </label>
          <div className="form-check">
            <input
              type="radio"
              className="form-check-input"
              id="hist-clone-preserve"
              name="hist-clone-mode"
              checked={!overwrite}
              onChange={() => {
                setOverwrite(false);
                setPreview(null);
              }}
            />
            <label className="form-check-label" htmlFor="hist-clone-preserve">
              Conservar el valor existente (solo rellenar huecos)
            </label>
          </div>
          <div className="form-check">
            <input
              type="radio"
              className="form-check-input"
              id="hist-clone-overwrite"
              name="hist-clone-mode"
              checked={overwrite}
              onChange={() => {
                setOverwrite(true);
                setPreview(null);
              }}
            />
            <label className="form-check-label" htmlFor="hist-clone-overwrite">
              Sobrescribir con los costos de la estación origen
            </label>
          </div>
        </div>

        {preview && (
          <div className="alert alert-info py-2 mb-0">
            <div>
              Días con costos en{" "}
              <strong>{locName(sourceLocId)}</strong>:{" "}
              <strong>{preview.totalDaysWithSource}</strong>
            </div>
            <div>
              Escrituras totales a realizar: <strong>{preview.totalWrites}</strong>
            </div>
            {targetLocIds.map((id) => (
              <div key={id} className="small">
                → {locName(id)}: {preview.perTarget[id]} día
                {preview.perTarget[id] === 1 ? "" : "s"}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-outline-secondary"
          onClick={handlePreview}
          disabled={previewLoading || loading}
        >
          {previewLoading ? "Calculando..." : "Previsualizar"}
        </button>
        <button
          className="btn btn-primary"
          onClick={handleClone}
          disabled={
            loading ||
            !sourceLocId ||
            targetLocIds.length === 0 ||
            (preview && preview.totalWrites === 0)
          }
        >
          {loading ? "Clonando..." : "Clonar costos"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
